import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { detectDevice } from '../utils/deviceDetection';
import { webAppInviteUrl } from '../utils/webApp';
import ContinueOnWeb from '../components/ContinueOnWeb';

const Invite = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const [showFallback, setShowFallback] = useState(false);
  const [copied, setCopied] = useState(false);

  const device = detectDevice();
  const isMobile = device === 'ios' || device === 'android';

  const inviteUrl = window.location.href;
  const deepLink = `sportsnest://invite?token=${encodeURIComponent(token || '')}`;
  const webUrl = webAppInviteUrl(token);

  const playStoreLink = () => {
    const base = t('downloads.googlePlayLink');
    const referrer = encodeURIComponent(`invite_token=${token || ''}`);
    return `${base}${base.includes('?') ? '&' : '?'}referrer=${referrer}`;
  };

  useEffect(() => {
    if (isMobile) {
      window.location.href = deepLink;
      const timer = setTimeout(() => setShowFallback(true), 2500);
      return () => clearTimeout(timer);
    }
  }, [isMobile, deepLink]);

  useEffect(() => {
    if (window.fbq) window.fbq('trackCustom', 'ViewInvitePage');
  }, []);

  const trackStoreClick = (platform) => {
    if (window.fbq) {
      window.fbq('track', 'Lead', { content_name: 'app_download', platform, source: 'invite' });
    }
  };

  // The clipboard carries the invite across install on iOS (no install
  // referrer there); the app checks the pasteboard on first launch.
  const handleAppStoreClick = () => {
    trackStoreClick('ios');
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(inviteUrl).then(() => setCopied(true)).catch(() => {});
    }
  };

  if (isMobile) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <img
            src="/logo192.png"
            alt="Tenku"
            className="w-20 h-20 mx-auto mb-8 rounded-2xl overflow-hidden"
          />

          {!showFallback ? (
            <>
              <h1 className="text-2xl font-bold text-white mb-4">
                {t('open.opening')}
              </h1>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto"></div>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-white mb-4">
                {t('invite.title')}
              </h1>
              <p className="text-gray-400 mb-8">
                {t('invite.subtitle')}
              </p>

              {device === 'ios' ? (
                <a
                  href={t('downloads.appStoreLink')}
                  onClick={handleAppStoreClick}
                  className="inline-flex items-center justify-center bg-brand hover:bg-brand-dark text-white font-bold py-4 px-8 rounded-xl text-lg transition-all duration-300 w-full mb-4"
                >
                  <svg className="h-6 w-6 mr-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                  {t('open.downloadAppStore')}
                </a>
              ) : (
                <a
                  href={playStoreLink()}
                  onClick={() => trackStoreClick('android')}
                  className="inline-flex items-center justify-center bg-brand hover:bg-brand-dark text-white font-bold py-4 px-8 rounded-xl text-lg transition-all duration-300 w-full mb-4"
                >
                  <svg className="h-6 w-6 mr-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                  </svg>
                  {t('open.downloadGooglePlay')}
                </a>
              )}

              {copied && (
                <p className="text-gray-500 text-sm mb-4">
                  {t('invite.copiedHint')}
                </p>
              )}

              <button
                onClick={() => window.location.href = deepLink}
                className="text-brand hover:text-brand-dark font-medium"
              >
                {t('open.tryAgain')}
              </button>

              <ContinueOnWeb href={webUrl} source="invite" />
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-6">
      <div className="max-w-lg w-full text-center">
        <img
          src="/logo192.png"
          alt="Tenku"
          className="w-20 h-20 mx-auto mb-8 rounded-2xl overflow-hidden"
        />

        <h1 className="text-3xl font-bold text-white mb-4">
          {t('invite.title')}
        </h1>
        <p className="text-gray-400 mb-8">
          {t('invite.desktopSubtitle')}
        </p>

        <div className="bg-white p-6 rounded-2xl inline-block mb-8">
          <QRCodeSVG
            value={inviteUrl}
            size={200}
            level="H"
            includeMargin={false}
          />
        </div>

        <p className="text-gray-500 text-sm mb-8">
          {t('open.scanQR')}
        </p>

        <ContinueOnWeb href={webUrl} source="invite" />
      </div>
    </div>
  );
};

export default Invite;
